/**
 * Aprobación HHEE (ASIS-5351) — service
 */

import { apiClient, API_CONFIG } from '../../core';
import { buildQueryString } from '../../shared/queryBuilder';
import type { ApiResponse } from '../../shared';
import {
  mapOvertimeApprovalCuts,
  mapOvertimeApprovalDetailPage,
  mapOvertimeApprovalPage,
  mapOvertimeJustificationTypes,
} from './overtimeApproval.mapper';
import type {
  LC_ApproveOvertimeDetailIn,
  LC_ApproveOvertimeDetailOut,
  LC_BulkApproveOvertimeIn,
  LC_BulkApproveOvertimeOut,
  LC_GetOvertimeApprovalDetailIn,
  LC_GetOvertimeApprovalDetailListOut,
  LC_GetOvertimeApprovalMonthlyCutsListOut,
  LC_GetOvertimeJustificationTypesListOut,
  LC_JustifyOvertimeDetailIn,
  LC_JustifyOvertimeIn,
  LC_JustifyOvertimeOut,
  LC_JustifyOvertimeDetailOut,
  LC_ListOvertimeApprovalIn,
  LC_ListOvertimeApprovalListOut,
  LC_ReverseOvertimeDetailIn,
  LC_ReverseOvertimeDetailOut,
  LC_ReverseOvertimeIn,
  LC_ReverseOvertimeOut,
  OvertimeApprovalCuts,
  OvertimeApprovalDetailPage,
  OvertimeApprovalPage,
  OvertimeJustificationType,
} from './overtimeApproval.types';


export class OvertimeApprovalService {
  /**
   * Resolves the monthly cuts that feed the Año / Mes / Descripción filters.
   * Omitting `filterCompanyId` means the "Todos" option: only holding-wide cuts.
   */
  async getMonthlyCuts(
    filterCompanyId?: number | null,
    signal?: AbortSignal,
  ): Promise<ApiResponse<OvertimeApprovalCuts>> {
    const query = buildQueryString(
      filterCompanyId ? { filterCompanyId } : {},
    );
    const endpoint = `${API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_MONTHLY_CUTS}${query}`;

    const response = await apiClient.get<LC_GetOvertimeApprovalMonthlyCutsListOut>(
      endpoint,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapOvertimeApprovalCuts(response.data) };
  }

  /**
   * Loads a page of the grid together with the cut's counters.
   * POST because the dimensional filters travel as up to seven CSV lists.
   */
  async list(
    params: LC_ListOvertimeApprovalIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<OvertimeApprovalPage>> {
    const response = await apiClient.post<LC_ListOvertimeApprovalListOut>(
      API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_SEARCH,
      params,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapOvertimeApprovalPage(response.data) };
  }

  /**
   * Approves the overtime hours of the selected employees within the monthly cut.
   * The response carries one outcome per employee plus the approved / skipped totals.
   */
  async approveBulk(
    params: LC_BulkApproveOvertimeIn,
  ): Promise<ApiResponse<LC_BulkApproveOvertimeOut>> {
    return apiClient.post<LC_BulkApproveOvertimeOut>(
      API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_BULK_APPROVE,
      params,
    );
  }

  /**
   * Reverses (deletes) the justified overtime hours of the selected employees within the monthly
   * cut. Used both by the unitary "Reversar" row action and a future bulk reverse.
   */
  async reverse(
    params: LC_ReverseOvertimeIn,
  ): Promise<ApiResponse<LC_ReverseOvertimeOut>> {
    return apiClient.post<LC_ReverseOvertimeOut>(
      API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_REVERSE,
      params,
    );
  }

  /**
   * Loads a page of the detail (one row per collaborator/jornada/tipo) for the employees selected
   * in the grid — a single row via the "eye" action or a batch via the bulk "Detalle" action.
   * Paginado server-side desde ASIS-5358 (`page`, `pageSize`, `searchText`, `orderBy`), para todos
   * los perfiles: acotar por `employeeIds` no impide que el corte traiga cientos de jornadas.
   */
  async getDetail(
    params: LC_GetOvertimeApprovalDetailIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<OvertimeApprovalDetailPage>> {
    const response = await apiClient.post<LC_GetOvertimeApprovalDetailListOut>(
      API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_DETAIL,
      params,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapOvertimeApprovalDetailPage(response.data) };
  }

  /**
   * Approves the hours edited row by row in the detail screen, with the mandatory comment that
   * lands on every affected record and on the audit trail (ASIS-5356). The response carries one
   * outcome per row plus the approved / skipped totals.
   */
  async approveDetail(
    params: LC_ApproveOvertimeDetailIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_ApproveOvertimeDetailOut>> {
    return apiClient.post<LC_ApproveOvertimeDetailOut>(
      API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_DETAIL_APPROVE,
      params,
      { signal },
    );
  }

  /**
   * Reverses the approved hours of the rows selected in the detail screen (ASIS-5357). No comment
   * is sent: reversing does not ask for one. The response carries one outcome per row plus the
   * reversed / skipped totals.
   */
  async reverseDetail(
    params: LC_ReverseOvertimeDetailIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_ReverseOvertimeDetailOut>> {
    return apiClient.post<LC_ReverseOvertimeDetailOut>(
      API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_DETAIL_REVERSE,
      params,
      { signal },
    );
  }

  /**
   * Catálogo de tipos de justificación del revisor (ASIS-5358). El backend lo gatea por flujo
   * habilitado + perfil efectivo `REVISOR` (403 en caso contrario) y devuelve **200 con lista
   * vacía** cuando el catálogo no tiene filas activas — nunca un error.
   */
  async getJustificationTypes(
    signal?: AbortSignal,
  ): Promise<ApiResponse<OvertimeJustificationType[]>> {
    const response = await apiClient.get<LC_GetOvertimeJustificationTypesListOut>(
      API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_JUSTIFICATION_TYPES,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapOvertimeJustificationTypes(response.data) };
  }

  /**
   * Marca como justificadas por el revisor las filas seleccionadas en el detalle (ASIS-5358), con
   * el tipo del catálogo y — solo para el tipo `OTRO` — su comentario libre. No viajan horas: las
   * resuelve el backend releyendo el detalle.
   */
  async justifyDetail(
    params: LC_JustifyOvertimeDetailIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_JustifyOvertimeDetailOut>> {
    return apiClient.post<LC_JustifyOvertimeDetailOut>(
      API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_DETAIL_JUSTIFY,
      params,
      { signal },
    );
  }

  /**
   * Justifica de forma masiva todas las horas del corte de los colaboradores seleccionados en el
   * listado (ASIS-5358). Contraparte de `approveBulk` para la mitad del flujo del revisor; el mismo
   * endpoint sirve a la acción unitaria del menú de fila, con un solo colaborador.
   *
   * No viajan tramos ni horas: el backend resuelve qué jornadas del corte justificar.
   */
  async justify(
    params: LC_JustifyOvertimeIn,
  ): Promise<ApiResponse<LC_JustifyOvertimeOut>> {
    return apiClient.post<LC_JustifyOvertimeOut>(
      API_CONFIG.ENDPOINTS.OVERTIME_APPROVAL_JUSTIFY,
      params,
    );
  }
}

export const overtimeApprovalService = new OvertimeApprovalService();
